interface TermsOfUseContentProps {
  backgroundColor?: string;
}

export function TermsOfUseContent({
  backgroundColor,
}: TermsOfUseContentProps = {}) {
  return (
    <div
      className={`prose prose-sm dark:prose-invert max-w-none space-y-12 ${backgroundColor || ""}`}
    >
      {/* Introduction */}
      <div className="rounded-lg bg-[#1a1a1a] p-4">
        <p className="font-arizona-sans text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          These Terms of Service (these &ldquo;Terms&rdquo;) are entered into by
          and between you and Symbolica AI, Inc. (&ldquo;Symbolica,&rdquo;
          &ldquo;Company,&rdquo; &ldquo;we,&rdquo; or &ldquo;us&rdquo;). The
          Terms, together with any documents expressly incorporated by reference
          herein, govern your access to and use of Symbolica&apos;s Agentica
          framework and the platform hosted by Symbolica (the &ldquo;Symbolica
          Platform&rdquo;), including all software development kits (SDKs),
          application programming interfaces (APIs), documentation, dashboards,
          hosted applications, chat interfaces, hosting services, and related
          services (collectively, the &ldquo;Services&rdquo;).
        </p>
        <p className="font-arizona-sans mt-4 text-[16px] font-semibold uppercase leading-[24px] text-[#FFFDF9]">
          BY CREATING AN ACCOUNT, ACCESSING OR USING THE SERVICES (INCLUDING AS
          AN ANONYMOUS USER), INSTALLING THE SDK, GENERATING OR USING AN API
          KEY, PURCHASING CREDITS, OR OTHERWISE ACCESSING OR USING THE SERVICES,
          YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND
          BY THESE TERMS AND THE COMPANY&apos;S PRIVACY POLICY, WHICH IS ALSO
          AVAILABLE ON THE SYMBOLICA WEBSITE (THE &ldquo;SITE&rdquo;). If you do
          not agree to these Terms, you must not access or use the Services. If
          you are entering into these terms on behalf of an entity, you
          represent and warrant that you have the legal authority to bind that
          entity to these terms, and that entity agrees to be bound by these
          Terms.
        </p>
      </div>

      {/* Section 1 - Account And Access */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          1. ACCOUNT AND ACCESS
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            <strong>1.1.</strong> Certain features of the Services may be
            accessed without creating an account, subject to usage limitations
            as displayed on the Services. To access additional features, you may
            create an account. If you create an account, you are solely
            responsible for maintaining the confidentiality and security of your
            account credentials and for all activities that occur under your
            account, whether or not authorized by you. You agree to notify us
            immediately of any unauthorized use of your account or any other
            breach of security. You acknowledge that you will be liable for
            losses incurred by Symbolica or any other user of the Services due
            to unauthorized use of your account.
          </p>
          <p>
            <strong>1.2.</strong> You may only use the Services in accordance
            with applicable law and these Terms. You may not share your account
            credentials with others or allow others to access your account
            except as expressly permitted by the Services.
          </p>
          <p>
            <strong>1.3.</strong> You may use the Services for your internal
            business purposes or to provide services to your end users. You may
            share sessions or outputs from the Services with others, including
            via shareable links or URLs. You are fully responsible for your end
            users&apos; and any recipients&apos; compliance with these Terms and
            will be liable for any violations by your end users or recipients as
            if you had committed such violations yourself.
          </p>
          <p>
            <strong>1.4.</strong> We may suspend or terminate your access to the
            Services if you violate these Terms, pose a security risk, fail to
            pay amounts due within the specified payment terms, engage in
            fraudulent activity, if required by law, or if we reasonably
            determine that continued access poses a risk to our systems, other
            users, or our business interests. We may act immediately and without
            prior notice in cases involving security concerns, legal
            requirements, material violations of these Terms, or fraudulent
            activity. We will provide notice where reasonably practicable,
            except where immediate action is necessary.
          </p>
          <p>
            <strong>1.5.</strong> You must comply with any usage limits, quotas,
            rate limits, or other restrictions associated with your account or
            subscription tier. We may implement technical measures to enforce
            such limits. Excessive or abusive usage may result in additional
            charges, throttling, suspension, or termination of your account.
          </p>
        </div>
      </section>

      {/* Section 2 - Description Of Services And Support */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          2. DESCRIPTION OF SERVICES AND SUPPORT
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            <strong>2.1.</strong> Agentica is a type-safe AI framework that
            enables LLM agents to integrate with your code, including functions,
            classes, live objects, and SDKs. The Symbolica Platform hosts
            Agentica, providing enterprise-scale infrastructure, security,
            sandboxing, hosting, management, and observability for agentic
            workloads. The Services include access to the Agentica framework,
            SDKs, APIs, the Symbolica Platform, hosted applications, chat
            interfaces, related documentation, dashboards, and tools provided by
            Symbolica. We reserve the right to modify the features,
            functionality, and specifications of the Services at any time. You
            should not rely on the Services for production or mission-critical
            use; we may discontinue, suspend, or terminate any or all hosted
            aspects of the Services (including hosted sessions, agents, and chat
            interfaces) at any time, and such features may become unavailable
            without notice.
          </p>
          <p>
            <strong>2.2.</strong> In connection with your use of the Services,
            we may facilitate your access to third-party AI model providers and
            inference services (&ldquo;Third-Party AI Services&rdquo;). When you
            use Third-Party AI Services through the Symbolica Platform, we act
            as an intermediary to arrange and facilitate your access to such
            services. Your use of Third-Party AI Services is subject to the
            applicable third-party provider&apos;s terms and conditions, and you
            are responsible for compliance with such terms. We do not provide,
            control, or guarantee the availability, performance, or results of
            Third-Party AI Services. Any fees for Third-Party AI Services
            represent amounts we collect on behalf of or pass through to the
            applicable third-party providers.
          </p>
          <p>
            <strong>2.3.</strong> We may, in our discretion, provide support
            services. Support services, if any, are provided on a commercially
            reasonable efforts basis.
          </p>
          <p>
            <strong>2.4.</strong> While we strive to provide reliable Services,
            you acknowledge that the Services may contain errors, bugs, or other
            issues, and that AI-generated outputs may be inaccurate, incomplete,
            or unsuitable for your purposes. You are responsible for testing,
            validating, and implementing appropriate safeguards for your use
            case.
          </p>
        </div>
      </section>

      {/* Section 3 - Restrictions And Responsibilities */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          3. RESTRICTIONS AND RESPONSIBILITIES
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            <strong>3.1.</strong> You shall not, directly or indirectly: (i)
            reverse engineer, decompile, disassemble or otherwise attempt to
            discover the source code, object code or underlying structure or
            algorithms of the Services or the underlying software
            (&ldquo;Software&rdquo;); (ii) modify, translate, or create
            derivative works based on the Services or Software (except to the
            extent expressly permitted by us or authorized within the Services);
            (iii) resell, sublicense, rent, lease, or otherwise commercialize
            access to the Services to third parties, or use the Services to
            provide service bureau, timesharing, or similar services to third
            parties, in each case without our prior written consent; (iv) remove
            any proprietary notices or labels; (v) misrepresent any output from
            the Services as entirely human-generated where disclosure is
            required by law or policy; (vi) attempt to circumvent quotas, usage
            caps, or billing mechanisms; (vii) use the Services in any manner
            that violates applicable laws or regulations; (viii) use the
            Services for any illegal, harmful, or abusive purposes, including to
            develop or train competing AI models or services, or to benchmark
            the Services against competing products or services; (ix) use the
            Services in connection with any application or system where failure
            or malfunction could reasonably be expected to lead to death,
            personal injury, or significant property or environmental damage;
            (x) attempt to gain unauthorized access to the Services, other
            users&apos; accounts, or our systems or networks, or conduct any
            security testing, penetration testing, or vulnerability scanning
            without our prior written consent; or (xi) without our prior written
            consent or a data processing addendum with us, submit or process
            through the Services any government-issued identifiers, precise
            health or biometric data, payment card primary account numbers, bank
            account numbers, children&apos;s data subject to child privacy laws,
            data protected by sector rules such as HIPAA or GLBA, or other data
            classified as sensitive or personally-identifiable by applicable
            privacy laws.
          </p>
          <p>
            <strong>3.2.</strong> You shall not remove or export from the United
            States or allow the export or re-export of the Services or Software,
            or any direct product thereof in violation of any restrictions, laws
            or regulations of the United States Department of Commerce, the
            United States Department of Treasury Office of Foreign Assets
            Control, or any other United States or foreign agency or authority.
            You represent and warrant that you are not (i) located in, or a
            resident or national of, any country subject to U.S. government
            embargo or designated as a &ldquo;terrorist supporting&rdquo;
            country, or (ii) on any U.S. government list of prohibited or
            restricted parties. You shall immediately notify us if you become
            subject to any such restrictions during the term of these Terms. As
            defined in FAR section 2.101, the Software and documentation are
            &ldquo;commercial items&rdquo; and according to DFAR section 252.227
            7014(a)(1) and (5) are deemed to be &ldquo;commercial computer
            software&rdquo; and &ldquo;commercial computer software
            documentation.&rdquo; Consistent with DFAR section 227.7202 and FAR
            section 12.212, any use modification, reproduction, release,
            performance, display, or disclosure of such commercial software or
            commercial software documentation by the U.S. Government will be
            governed solely by these Terms and will be prohibited except to the
            extent expressly permitted by these Terms.
          </p>
          <p>
            <strong>3.3.</strong> You represent, covenant, and warrant that (i)
            your performance under these Terms, including but not limited to any
            use of the Services, will be in compliance with all applicable laws
            and regulations; (ii) you have obtained all necessary rights,
            licenses, consents, and permissions to submit Your Content to the
            Services and grant the licenses set forth herein; (iii) Your Content
            does not and will not infringe, misappropriate, or violate any third
            party&apos;s intellectual property rights, privacy rights, publicity
            rights, or other proprietary rights.
          </p>
        </div>
      </section>

      {/* Section 4 - Intellectual Property And Related Rights */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          4. INTELLECTUAL PROPERTY AND RELATED RIGHTS
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            <strong>4.1.</strong> Except as otherwise set forth herein, we and
            our subcontractors and licensors are the sole and exclusive owners
            of all rights and interests in all (a) patents, patent applications;
            (b) registered and unregistered trademarks, service marks, trade
            names, domain names, and all of the associated goodwill; (c)
            registered and unregistered copyrights and all other literary and
            author&apos;s rights or moral rights; (d) trade secrets, know-how,
            show-how, concepts, ideas, methods, processes, designs, discoveries,
            improvements, and inventions, whether patentable or unpatentable;
            (e) all other intellectual, industrial, and proprietary rights now
            or hereafter coming into existence throughout the world; (f)
            applications for and registrations, renewals, and extensions of any
            of the foregoing; and (g) exclusive and non-exclusive license rights
            to any of the foregoing (collectively &ldquo;Intellectual
            Property&rdquo;) and other proprietary rights and interests in and
            to the Services and Software and any documentation or data related
            thereto. We own and shall retain all right, title and interest in
            and to all improvements, enhancements or modifications to the
            Services or Software, and all Intellectual Property rights related
            to any of the foregoing.
          </p>
          <p>
            <strong>4.2.</strong> We shall have the right to collect and analyze
            data and other information relating to the provision, use and
            performance of various aspects of the Services, Software, and
            related systems and technologies (including, without limitation,
            anonymous and aggregated information concerning use of the
            Services), and we will be free (during and after the term hereof) to
            (i) use such information and data to improve and enhance the
            Services and for other development, diagnostic and corrective
            purposes in connection with the Services and other of our offerings
            (including to train our models), and (ii) disclose such data solely
            in aggregate or other de-identified form in connection with its
            business. No rights or licenses are granted except as expressly set
            forth herein.
          </p>
          <p>
            <strong>4.3.</strong> You own all right, title, and interest in and
            to prompts, data, and instructions you submit to the Services
            (&ldquo;Input&rdquo;). We own all right, title, and interest in and
            to any results returned by the Services from your Input
            (&ldquo;Output&rdquo; and, together with Input, &ldquo;Your
            Content&rdquo;); however, we provide you a worldwide, perpetual,
            irrevocable license to use, reproduce, modify, distribute, display,
            perform, and commercialize Outputs for any purpose. You grant us a
            worldwide, royalty-free license to host, process, transmit, display,
            and store Your Content to provide, maintain, secure, improve, and
            support the Service (including to train our models) and to comply
            with law.
          </p>
          <p>
            <strong>4.4.</strong> If you enable integrations or select third
            party model providers on the Services, you authorize us to share
            Your Content with those providers to the extent needed to provide
            the Services to you. Those providers&apos; terms govern their
            services and use of Your Content, and we are not responsible for the
            acts or inactions of such providers. Disable an integration in your
            account to stop sharing going forward.
          </p>
        </div>
      </section>

      {/* Section 5 - Fees */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          5. FEES
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            <strong>5.1.</strong> Fees (&ldquo;Fees&rdquo;) are based on actual
            usage and may include: (i) Inference Fees, which are charges for
            third-party AI models accessed through the Services, and (ii)
            Hosting/Platform Fees, which are charges for our hosting,
            orchestration, management, security, sandboxing, observability, and
            related Services that we provide directly to you.
          </p>
          <p>
            <strong>5.2.</strong> You may access certain Services on a limited
            trial basis with promotional credits (&ldquo;Free Credits&rdquo;),
            which are subject to usage caps and restrictions. You may purchase
            prepaid service credits (&ldquo;Credits&rdquo;) at the price
            displayed on the Services, or subscribe to a paid tier that grants a
            monthly allocation of Credits. Credits may be used across any of the
            Services, and all Services draw from the same Credit balance
            associated with your account. By purchasing Credits or subscribing
            to a paid tier, you authorize us to charge your payment method on
            file for the purchase or subscription amount. Subscriptions renew
            monthly and grant the applicable Credit allocation each billing
            period. You may also occasionally receive promotional Credits from
            us. Unless otherwise provided on the Services, all Credits, whether
            purchased, subscription-based, or promotional, (i) may have other
            terms and conditions, as described on your account or in the
            Services (i.e., they may only be applicable for certain types of
            Fees), (ii) expire twelve (12) months after the date they are
            applied to your account (or, for subscription Credits, at the end of
            the subscription period in which they were granted, whichever is
            shorter), and (iii) have no cash value and will not be refunded in
            the event of account termination. Expired Credits cannot be
            reinstated. If payment fails or your prepaid balance is exhausted,
            we may suspend access to the Services until payment is received or
            your account is replenished.
          </p>
          <p>
            <strong>5.3.</strong> You are responsible for all taxes associated
            with your use of the Services (excluding taxes on our net income).
            If we are required to collect or pay taxes on your behalf, such
            amounts will be added to the applicable Fees at the time of
            purchase.
          </p>
          <p>
            <strong>5.4.</strong> We reserve the right to audit and monitor your
            usage of the Services to verify accurate billing and compliance with
            these Terms. You agree to provide reasonable cooperation with any
            such audit.
          </p>
        </div>
      </section>

      {/* Section 6 - Termination */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          6. TERMINATION
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            We may suspend or terminate access immediately for violation of
            these Terms, security risk, exhaustion of prepaid Credits, to comply
            with law, or for any other reason. You may terminate at any time by
            closing your account.
          </p>
        </div>
      </section>

      {/* Section 7 - Warranty And Disclaimer */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          7. WARRANTY AND DISCLAIMER
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p className="font-semibold uppercase">
            WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR
            FREE; NOR DO WE MAKE ANY WARRANTY AS TO THE RESULTS THAT MAY BE
            OBTAINED FROM USE OF THE SERVICES. THE SERVICES AND IMPLEMENTATION
            SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND WE DISCLAIM ALL
            WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO,
            IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p className="font-semibold uppercase">
            WE DO NOT WARRANT THAT ANY OUTPUT FROM THE SERVICES IS ACCURATE,
            COMPLETE, RELIABLE, CURRENT, OR SUITABLE FOR YOUR USE CASE. YOU ARE
            SOLELY RESPONSIBLE FOR REVIEWING, VERIFYING, AND VALIDATING ALL
            OUTPUT FROM THE SERVICES BEFORE USE. YOU ACKNOWLEDGE THAT THE
            SERVICES MAY PRODUCE ERRORS, INACCURACIES, OR INAPPROPRIATE CONTENT,
            AND YOU ASSUME ALL RISKS ASSOCIATED WITH YOUR USE OF ANY OUTPUT. YOU
            AGREE NOT TO RELY ON THE SERVICES FOR ANY CRITICAL, SAFETY-CRITICAL,
            OR HIGH-RISK APPLICATIONS WITHOUT APPROPRIATE HUMAN OVERSIGHT AND
            INDEPENDENT VERIFICATION.
          </p>
        </div>
      </section>

      {/* Section 8 - Limitation Of Liability */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          8. LIMITATION OF LIABILITY
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p className="font-semibold uppercase">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL SYMBOLICA,
            ITS DIRECTORS, EMPLOYEES, AGENTS, PARTNERS, SUPPLIERS, LICENSORS, OR
            AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
            CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION,
            LOSS OF PROFITS, DATA, USE, GOODWILL, BUSINESS INTERRUPTION, COST OF
            SUBSTITUTE SERVICES, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I)
            YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICES;
            (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON OR THROUGH THE
            SERVICES, INCLUDING WITHOUT LIMITATION, ANY DEFAMATORY, OFFENSIVE OR
            ILLEGAL CONDUCT OF OTHER USERS OR THIRD PARTIES; (III) ANY CONTENT
            OR OUTPUT OBTAINED FROM THE SERVICES; (IV) UNAUTHORIZED ACCESS, USE
            OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT; (V) ANY ERRORS,
            INACCURACIES, OR OMISSIONS IN THE SERVICES OR OUTPUT; (VI) ANY
            DECISIONS MADE OR ACTIONS TAKEN IN RELIANCE ON THE SERVICES OR
            OUTPUT; AND/OR (VII) ANY SECURITY BREACH OR DATA LOSS, WHETHER BASED
            ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE) OR ANY OTHER
            LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE
            POSSIBILITY OF SUCH DAMAGE, AND EVEN IF A REMEDY SET FORTH HEREIN IS
            FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE. IN NO EVENT WILL
            SYMBOLICA&apos;S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT
            OF OR RELATING TO THESE TERMS OR THE SERVICES EXCEED THE GREATER OF
            (A) THE TOTAL HOSTING/PLATFORM FEES PAID BY YOU TO SYMBOLICA IN THE
            TWELVE (12) MONTHS PRECEDING THE CLAIM OR (B) ONE HUNDRED DOLLARS
            ($100). YOUR USE OF THE SERVICES AND RELIANCE ON ANY INFORMATION OR
            OUTPUT FROM THE SERVICES IS SOLELY AT YOUR OWN RISK. FOR CLARITY,
            YOU AUTHORIZE AND ASSUME ALL RISK FOR ANY ACTIONS TAKEN BY AGENTS
            YOU CONFIGURE OR RUN THROUGH THE SERVICES, INCLUDING IRREVERSIBLE
            ACTIONS (E.G., SEND, MODIFY, OR DELETE) IN CONNECTED THIRD-PARTY
            SYSTEMS. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION
            OF INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATIONS MAY
            NOT APPLY TO YOU.
          </p>
        </div>
      </section>

      {/* Section 9 - Indemnification */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          9. INDEMNIFICATION
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            You agree to indemnify, defend, and hold harmless Symbolica, its
            affiliates, and their respective directors, officers, employees,
            agents, and representatives from and against any and all third party
            claims, demands, losses, liabilities, damages, suits, fees,
            judgments, costs and expenses (including reasonable attorneys&apos;
            fees) which arise out of or relate to: (i) your breach of these
            Terms; (ii) your use or misuse of the Services; (iii) your violation
            of any law or regulation; (iv) your violation of any third party
            rights, including intellectual property rights, privacy rights, or
            contractual rights; (v) Your Content or any content you submit,
            post, or transmit through the Services; (vi) any decisions made or
            actions taken by you in reliance on output from the Services; or
            (vii) any claim that Your Content or your use of the Services
            infringes or misappropriates any third party intellectual property
            rights. We reserve the right, at our own expense, to assume the
            exclusive defense and control of any matter subject to
            indemnification by you, and you agree to cooperate with our defense
            of such claims.
          </p>
        </div>
      </section>

      {/* Section 10 - Dispute Resolution */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          10. DISPUTE RESOLUTION
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            Any dispute arising from these Terms or use of the Services will be
            resolved by binding arbitration administered by JAMS under its
            applicable rules, with proceedings in San Francisco, California,
            unless otherwise agreed. Either party may bring qualifying small
            claims in small claims court. Claims will be brought only on an
            individual basis. By agreeing to these Terms, you and we each waive
            the right to a trial by jury or to participate in a class action.
          </p>
        </div>
      </section>

      {/* Section 11 - Suspension And Termination */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          11. SUSPENSION AND TERMINATION
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            We may suspend or terminate access immediately for violation of
            these Terms, security risk, non-payment, or to comply with law. You
            may terminate at any time by closing your account. Upon termination
            we will delete or de-identify Your Content from active systems
            within 30 days, subject to legal holds and backups. Sections
            intended to survive termination of these Terms will so survive.
          </p>
        </div>
      </section>

      {/* Section 12 - Trademark */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          12. TRADEMARK
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            The Company name, the terms, and all related names, logos, product
            and service names, designs, and slogans are trademarks of Symbolica
            or its affiliates or licensors. You must not use such marks without
            the prior written permission of the Company. All other names, logos,
            products and service names, designs, and slogans on the Services are
            the trademarks of their respective owners.
          </p>
        </div>
      </section>

      {/* Section 13 - Claims Of Copyright Infringement */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          13. CLAIMS OF COPYRIGHT INFRINGEMENT
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            We respect intellectual property rights and respond to notices under
            the Digital Millennium Copyright Act. Send notices to: DMCA Agent,
            Symbolica AI, Inc., 447 Sutter Street, Suite 506-1212, San
            Francisco, California 94108, hello@symbolica.ai. Your notice must
            include the information required by 17 U.S.C. 512(c)(3).
          </p>
        </div>
      </section>

      {/* Section 14 - Miscellaneous */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          14. MISCELLANEOUS
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            All matters relating to the Services and these Terms, and any
            dispute or claim arising therefrom or related thereto (in each case,
            including non-contractual disputes or claims), shall be governed by
            and construed in accordance with the internal laws of the State of
            California without giving effect to any choice or conflict of law
            provisions or rule. We may modify these Terms from time to time. If
            we make material changes to these Terms, we will notify you by email
            (if you have provided an email address) and/or by posting a
            prominent notice on the Services at least thirty (30) days before
            the changes take effect. For changes to fees or charges for
            subscription services, we will provide notice at least seven (7)
            days and no more than thirty (30) days before the changes take
            effect, as required by California law. Material changes include, but
            are not limited to, changes to pricing, payment terms, data usage
            rights, limitation of liability, dispute resolution mechanisms, or
            the scope of Services. Non-material changes (such as clarifications,
            corrections of typos, or changes required by law) may be made
            without advance notice. When we post changes, we will revise the
            &ldquo;Last Modified&rdquo; date at the top of these Terms. Your
            continued access to or use of the Services after the effective date
            of the revised Terms constitutes your acceptance of the changes. If
            you do not agree to the modified Terms, you must stop using the
            Services and may close your account before the effective date
            without penalty. If you have a paid subscription and you close your
            account due to material changes we make to these Terms, you may be
            entitled to a prorated refund of any prepaid fees for the unused
            portion of your subscription term, provided you close your account
            within thirty (30) days of our notice of the material change. These
            Terms constitute the entire agreement between us regarding the
            Services and supersede and merge any prior proposals,
            understandings, and contemporaneous communications; provided,
            however, that if you execute an enterprise agreement with us, that
            enterprise agreement shall control (i) to the extent these Terms
            conflict with such enterprise agreement and (ii) in the event and to
            the extent such agreement itself specifies that it should take
            precedence. If any provision of these Terms is held to be invalid or
            unenforceable, that provision shall be limited or eliminated to the
            minimum extent necessary so that these Terms shall otherwise remain
            in full force and effect. No waiver of any provision of these Terms
            shall be effective unless in writing and signed by the party against
            whom such waiver is sought to be enforced. The failure of either
            party to enforce its rights under these Terms at any time for any
            period will not be construed as a waiver of such rights.
          </p>
        </div>
      </section>

      {/* Section 15 - Contact Information */}
      <section>
        <h2 className="font-arizona-sans mb-6 border-b pb-2 text-[22px] font-light uppercase leading-[100%] tracking-[-0.44px] text-[#FFFDF9]">
          15. CONTACT INFORMATION
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            If you have any questions, complaints, or claims with respect to the
            Site or Services, you may contact us at hello@symbolica.ai.
          </p>
        </div>
      </section>

      {/* SMS Messaging Disclaimer */}
      <section>
        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <p>
            All the above categories exclude text messaging originator opt-in
            data and consent; this information will not be shared with any third
            parties. Message and data rates may apply. Frequency varies. Reply
            STOP to opt-out.
          </p>
        </div>
      </section>
    </div>
  );
}
